/**
 * Simulation cadence.
 *
 * ADR-001 §5 D1 decided that level of detail belongs to the canonical
 * simulation rather than to the renderer: the authoritative loop runs every ant
 * individually but at a coarser tick, and the browser adds smoothing that
 * changes no state. ADR-002 fixes the coarse rate at 6 Hz and makes it a world
 * parameter.
 *
 * That only works if the simulation's behaviour is a function of *elapsed sim
 * time* rather than of *how many times `update()` happened to be called*. A
 * quantity that advances by one per tick — an ant's age, a per-tick dice roll —
 * silently becomes ten times faster or slower when the rate moves, and the
 * cadence stops being a fidelity knob and starts being a game-rule change.
 *
 * This module supplies the three things needed to keep that from happening:
 *
 *   - `SimClock`      — the canonical tick counter and the rate it runs at
 *   - `refTicks`      — the conversion from a timestep to reference ticks
 *   - `advanceTimer`  — an interval accumulator that does not lose its remainder
 *
 * Everything is defined relative to `REFERENCE_HZ`, the rate the existing
 * constants were authored against. That is deliberate: at 60 Hz every helper
 * here is an identity, so the conversion is provably a no-op for the game as it
 * shipped, and the recorded golden masters keep their meaning.
 *
 * See `docs/simulation-cadence.md`.
 */

import { Rng } from './Rng';

/**
 * The rate the simulation's constants were written against.
 *
 * Ant lifespans, per-tick probabilities and movement speeds were all tuned by
 * hand at 60 Hz. Rather than re-tune several dozen constants — which would be a
 * balance change smuggled inside an architecture change — rate-sensitive
 * quantities are expressed in *reference ticks* and scaled by the timestep.
 */
export const REFERENCE_HZ = 60;

/** One reference tick, in sim-milliseconds. */
export const REFERENCE_TICK_MS = 1000 / REFERENCE_HZ;

/**
 * The canonical simulation rate.
 *
 * Spike B measured a full-fidelity district-season at 26.5 s of CPU against a
 * 5 s budget (ADR-001 §3). Six hertz is the cadence at which the colony still
 * makes every decision individually and a season fits the budget.
 *
 * This is a **world parameter**, not a client preference. Client and server run
 * the same value or they run different simulations; it is not exposed in the
 * settings UI for that reason.
 */
export const CANONICAL_SIM_HZ = 6;

/**
 * The rate the shipped client currently runs at.
 *
 * Still 60 while the renderer has no interpolation: dropping the client to 6 Hz
 * without smoothing would make ants visibly teleport. The sim is rate-invariant
 * either way — this constant is what the renderer is ready for, not what the
 * simulation requires. See ADR-002 §5 D6.
 */
export const DEFAULT_SIM_HZ = REFERENCE_HZ;

/**
 * How many reference ticks a timestep represents.
 *
 * The conversion factor for every quantity that used to advance by one per
 * tick. Exactly 1 at the reference rate.
 */
export function refTicks(dt: number): number {
  return dt / REFERENCE_TICK_MS;
}

/**
 * Rescale a per-reference-tick probability to a timestep.
 *
 * `p` is the chance the event fires in one reference tick. Over `n` reference
 * ticks the chance of *no* event is `(1 - p)^n`, so the equivalent single-roll
 * probability is `1 - (1 - p)^n`. Using the exact form rather than `p * n`
 * matters at coarse rates and for large `p`, where the linear approximation
 * would exceed 1 and turn a possibility into a certainty.
 *
 * The expected number of events per sim-second is preserved; the *clustering*
 * is not, since a coarse tick can only fire once. That is the honest limit of
 * cadence-independence and it is why the rate is a world parameter rather than
 * a per-tier setting.
 */
export function scaleChance(p: number, dt: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  const n = refTicks(dt);
  if (n === 1) return p;
  return 1 - Math.pow(1 - p, n);
}

/**
 * Roll a per-reference-tick probability over a timestep.
 *
 * Consumes exactly one step from the stream at any rate, so the draw count per
 * tick is a property of the code path rather than of the cadence.
 */
export function rollPerRefTick(rng: Rng, p: number, dt: number): boolean {
  return rng.chance(scaleChance(p, dt));
}

/** The result of advancing an interval accumulator. */
export interface TimerAdvance {
  /** The accumulator's new value, carrying the remainder. */
  value: number;
  /** How many whole intervals elapsed during this timestep. */
  fires: number;
}

/**
 * Advance a periodic timer without losing its remainder.
 *
 * The pattern this replaces —
 *
 * ```ts
 * timer += dt;
 * if (timer >= INTERVAL) { timer = 0; doTheThing(); }
 * ```
 *
 * — quantises the period to a whole number of ticks and throws away the
 * overshoot. At 60 Hz that costs a few milliseconds and nobody notices. At 6 Hz
 * a 1,000 ms interval lands on a tick boundary only if floating-point addition
 * cooperates, and when it does not the period stretches to 1,167 ms — a 17%
 * error in something as load-bearing as how fast the colony eats.
 *
 * Subtracting the interval instead of zeroing keeps the long-run period exact
 * and self-corrects a late fire on the following one.
 *
 * `maxFires` bounds the catch-up when a timestep spans several intervals, so a
 * pathologically coarse rate degrades into a slower period rather than into a
 * burst of work inside one tick.
 */
export function advanceTimer(
  timer: number,
  dt: number,
  intervalMs: number,
  maxFires = 4
): TimerAdvance {
  if (!(intervalMs > 0)) return { value: 0, fires: 1 };

  let value = timer + dt;
  let fires = 0;
  while (value >= intervalMs && fires < maxFires) {
    value -= intervalMs;
    fires++;
  }
  // Dropped fires must not accumulate, or the timer runs away and the system
  // fires every tick forever after.
  if (value >= intervalMs) value = value % intervalMs;

  return { value, fires };
}

/** A `SimClock`'s complete state, for snapshots. */
export interface SimClockState {
  tick: number;
  elapsedMs: number;
}

/**
 * The canonical clock.
 *
 * The simulation's only source of time. Nothing in a sim path may read
 * `Date.now()` or `performance.now()` — an outcome must not depend on when it
 * was computed or on how long a frame took (`docs/determinism.md`, rule 2).
 */
export class SimClock {
  /** Ticks per sim-second. */
  readonly hz: number;

  /** One tick, in sim-milliseconds. The `dt` every `update()` receives. */
  readonly tickMs: number;

  private _tick = 0;
  private _elapsedMs = 0;

  constructor(hz: number = DEFAULT_SIM_HZ) {
    if (!Number.isFinite(hz) || hz <= 0) {
      throw new RangeError(`SimClock rate must be a positive number, got ${hz}`);
    }
    this.hz = hz;
    this.tickMs = 1000 / hz;
  }

  /** Ticks elapsed since the world began. Monotonic, never reset. */
  get tick(): number {
    return this._tick;
  }

  /** Sim-milliseconds elapsed. Accumulated, not derived, so it survives a rate change mid-world. */
  get elapsedMs(): number {
    return this._elapsedMs;
  }

  /** How many reference ticks one tick of this clock represents. */
  get refTicksPerTick(): number {
    return refTicks(this.tickMs);
  }

  /** Advance one tick. Returns the timestep to hand to `update()`. */
  advance(): number {
    this._tick++;
    this._elapsedMs += this.tickMs;
    return this.tickMs;
  }

  /** Ticks needed to cover a duration in sim-seconds, rounded to a whole tick. */
  ticksForSeconds(seconds: number): number {
    return Math.round(seconds * this.hz);
  }

  save(): SimClockState {
    return { tick: this._tick, elapsedMs: this._elapsedMs };
  }

  load(state: SimClockState): void {
    this._tick = state.tick;
    this._elapsedMs = state.elapsedMs;
  }
}

/**
 * How many district budget cycles make a season.
 *
 * The design document's definition, and the one the code uses:
 *
 * > A season equals a fixed number of district budget cycles (default:
 * > 1 season = 90 cycles of the existing 10-second cycle). — §4
 *
 * A count of cycles rather than a count of ticks, because the cadence is a world
 * parameter and a cycle is not: the treasury settles ninety times over 900
 * sim-seconds whether that is 54,000 ticks or 5,400. The season inherits the
 * cadence-invariance the budget cycle already has.
 *
 * A Charter parameter eventually (design §17); a constant until Phase 7 gives
 * Charters somewhere to live.
 */
export const CYCLES_PER_SEASON = 90;

/**
 * The budget cycle's period, in sim-milliseconds.
 *
 * Restated here because `ticksPerSeason` needs it and `EconomyConfig` — where it
 * lives as `UBI_INTERVAL`, and where a settings slider can move it — is surface
 * code this module must not depend on. The two agree by default and the
 * cadence tests would notice if they stopped.
 */
export const DEFAULT_CYCLE_MS = 10_000;

/**
 * A season in ticks, at a given rate.
 *
 * `CYCLES_PER_SEASON` cycles of `cycleMs` each (design document §4). The tick
 * count changes with the cadence; the sim duration does not. A world running a
 * non-default budget period passes its own `cycleMs` — the season is ninety
 * cycles either way, and it is only this conversion that moves.
 */
export function ticksPerSeason(
  hz: number = DEFAULT_SIM_HZ,
  cycleMs: number = DEFAULT_CYCLE_MS
): number {
  return Math.round(CYCLES_PER_SEASON * (cycleMs / 1000) * hz);
}
